import { EmptyState, Grid, GridColumn, GridRow, LazyLoader, Select, SelectOptionType } from '@bp/ui-components';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { graphql } from 'client/gql';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { UseQueryExecute, useMutation, useQuery } from 'urql';
import {
  SettingsImportPlanungSubpageDataQuery,
  SortDirection,
  UpdateBpSchoolyearDocument,
} from '../../../client/gql/graphql';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import {
  DesktopNavigationTabs,
  type DesktopNavigationTabsType,
} from '../../../components/NavigationTabs/Desktop/DesktopNavigationTabs';
import type { PlanungBaseDataType, PlanungSchoolyears } from '../../../components/PlanungImport/types';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { backendApi } from '../../../utils/backendApi';
import { createForeignRefId, SyncMaster } from '../../../utils/create-foreignRefId';
import { InstitutionSchoolyearsSubpageQueryDocument } from '../../Institution/subpages/InstitutionSchoolyearsSubpage';

const SettingsImportPlanungSubpageDocument = graphql(`
  query SettingsImportPlanungSubpageData($schoolYearUuid: ID!, $organizationUuid: ID!, $lessonJobWhere:LessonJobWhere, $lessonJobOptions:LessonJobOptions) {
    ...PlanungImportTeachersFormFields
    ...PlanungImportSubjectsFormFields
    ...PlanungImportClassesFormFields
    ...PlanungImportLessonsFormFields
    ...ForeignSyncMetasFields
    ...LessonJobsTableFields
  }
`);

export type SettingsImportPlanungSubpageOutletContext = {
  bpData: SettingsImportPlanungSubpageDataQuery | undefined;
  planungData: PlanungBaseDataType | undefined;
  refetch: UseQueryExecute;
  planungSchoolyear: string;
  bpSchoolyear: string;
  organizationUuid: string;
};

export const SettingsImportPlanungSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [planungSchoolyear, setPlanungSchoolyear] = useState<string | null>(null);
  const [bpSchoolyear, setBpSchoolyear] = useState<string | null>(null);

  const [{ data: bpData, fetching }, refetch] = useQuery({
    query: SettingsImportPlanungSubpageDocument,
    variables: {
      schoolYearUuid: bpSchoolyear ?? '',
      organizationUuid,
      lessonJobWhere: {
        organizationUuid,
      },
      lessontJobOptions: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
      eventJobWhere: {
        organizationUuid,
      },
      eventJobOptions: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
    },
  });

  const [{ data: schoolyearsData }] = useQuery({
    query: InstitutionSchoolyearsSubpageQueryDocument,
    variables: {
      organizationUuid,
    },
  });

  const [, connectSchoolYears] = useMutation(UpdateBpSchoolyearDocument);

  const perms = usePermissionChecker();

  const canImportPlanung = perms.canImport({ uuid: organizationUuid });

  const matches = useMatches();
  const last = [...matches].pop();

  const tabs: DesktopNavigationTabsType[] = [
    {
      title: t('teachers.title'),
      path: `teachers`,
      active: last?.id === 'planung-teachers',
      disabled: !planungSchoolyear || !bpSchoolyear,
    },
    {
      title: t('subjects.title', { count: 2 }),
      path: `subjects`,
      disabled: !planungSchoolyear || !bpSchoolyear,
    },
    {
      title: t('classes.title', { count: 2 }),
      path: `classes`,
      disabled: !planungSchoolyear || !bpSchoolyear,
    },
    {
      title: t('courses.title'),
      path: `lessons`,
      disabled: !planungSchoolyear || !bpSchoolyear,
    },
    {
      title: t('appointments.title'),
      path: `events`,
      disabled: !planungSchoolyear || !bpSchoolyear,
    },
  ];

  const { data: planungSchoolyears } = useReactQuery<PlanungSchoolyears>({
    queryKey: ['planung', 'schoolyears'],
    queryFn: async () => {
      const response = await backendApi.fetch(`/planung/schoolyears`);
      if (response.ok) return await response.json();
      if (!response.ok) return [];
    },
  });

  const { data: planungData } = useReactQuery<PlanungBaseDataType>({
    queryKey: ['planung', planungSchoolyear],
    queryFn: async () => {
      const response = await backendApi.fetch(`/planung/entities/${planungSchoolyear ?? ''}`);
      return await response.json();
    },
    enabled: !!planungSchoolyear,
  });

  const planungSchoolyearOptions = useCreateSelectOptions(planungSchoolyears ?? [], 'uuid', 'name');

  const bpSchoolyearOptions = useCreateSelectOptions(schoolyearsData?.schoolYears ?? [], 'uuid', 'name');

  const connect = async (planungYearUuid: string | null, bpYearUuid: string | null) => {
    if (!planungYearUuid || !bpYearUuid) return;
    const planungSchoolYearForeignRefId = createForeignRefId(SyncMaster.planung, planungYearUuid);

    const oldConnectedBpSchoolYear = schoolyearsData?.schoolYears.find((sy) =>
      sy.foreignRefId?.includes(planungSchoolYearForeignRefId),
    );

    if (oldConnectedBpSchoolYear) {
      // disconnect originally connected schoolyears
      await connectSchoolYears({
        uuid: bpYearUuid,
        update: {
          foreignRefId: oldConnectedBpSchoolYear.foreignRefId?.filter(
            (refId) => refId !== planungSchoolYearForeignRefId,
          ),
        },
      });
    }

    // connect schoolyears
    await connectSchoolYears({
      uuid: bpYearUuid,
      update: {
        foreignRefId: Array.from(
          // make 'em unique
          new Set([
            ...(schoolyearsData?.schoolYears
              .find((sy) => sy.uuid === bpYearUuid)
              ?.foreignRefId?.filter((fri) => !fri.includes('planung::')) ?? []),
            planungSchoolYearForeignRefId,
          ]),
        ),
      },
    });
  };

  return (
    <BpSubpage isForbidden={!canImportPlanung}>
      <BpCard header={{ headline: t('planungImport.pickYears') }}>
        <Grid>
          <GridRow>
            <GridColumn width={6}>
              <Select
                label={t('planungImport.planungYear')}
                options={planungSchoolyearOptions}
                name={'schoolYears-planung'}
                onChange={async (event) => {
                  const yearUuid = (event as SingleValue<SelectOptionType>)?.value as string;
                  setPlanungSchoolyear(yearUuid);
                  await connect(yearUuid, bpSchoolyear);
                }}
                value={planungSchoolyearOptions.find((o) => o.value === planungSchoolyear)}
                menuPosition='fixed'
              />
            </GridColumn>
            <GridColumn width={6}>
              <Select
                label={t('planungImport.bpYear')}
                options={bpSchoolyearOptions}
                name={'schoolYears-bp'}
                onChange={async (event) => {
                  const yearUuid = (event as SingleValue<SelectOptionType>)?.value as string;
                  setBpSchoolyear(yearUuid);
                  await connect(planungSchoolyear, yearUuid);
                }}
                value={bpSchoolyearOptions.find((o) => o.value === bpSchoolyear)}
                menuPosition='fixed'
              />
            </GridColumn>
          </GridRow>
        </Grid>
      </BpCard>

      <div className='mt-6'>
        <DesktopNavigationTabs tabs={tabs} className='mb-4' />
        {!planungSchoolyear || !bpSchoolyear ? (
          <EmptyState forcedHeight='40vh' title={t('planungImport.selectYears')} />
        ) : (
          <Suspense fallback={<LazyLoader embedded transparent forceHeight='40vh' />}>
            <Outlet
              context={{
                bpData,
                planungData,
                refetch,
                fetching,
                planungSchoolyear,
                bpSchoolyear,
                organizationUuid,
              }}
            />
          </Suspense>
        )}
      </div>
    </BpSubpage>
  );
};
